import type { StyleSpecification } from "maplibre-gl";
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import * as React from "react";
import { useFeature } from "store/selectors/hooks";
import { reportError } from "util/error-tracking";
import {
    BASEMAP_MANIFEST_URL,
    BasemapManifest,
    buildSelfHostedMapStyle,
    MAPTILER_STYLE_URL,
    parseBasemapManifest,
} from "./map-style";

let pmtilesProtocolRegistered = false;

const ensurePmtilesProtocol = (): void => {
    if (pmtilesProtocolRegistered) {
        return;
    }
    maplibregl.addProtocol("pmtiles", new Protocol().tile);
    pmtilesProtocolRegistered = true;
};

const fetchBasemapManifest = async (): Promise<BasemapManifest> => {
    const response = await fetch(BASEMAP_MANIFEST_URL);
    if (!response.ok) {
        throw new Error(`Basemap manifest request failed: ${response.status}`);
    }
    return parseBasemapManifest(await response.json());
};

let manifestPromise: Promise<BasemapManifest> | null = null;

export const TEST_resetBasemapManifestCache = (): void => {
    manifestPromise = null;
};

const loadBasemapManifest = (): Promise<BasemapManifest> => {
    if (!manifestPromise) {
        manifestPromise = fetchBasemapManifest().catch((e: Error) => {
            manifestPromise = null;
            throw e;
        });
    }
    return manifestPromise;
};

export type BasemapPolicy = "allowThirdPartyFallback" | "selfHostedOnly";

export type MapStyleResult =
    | { status: "ready"; style: string | StyleSpecification }
    | { status: "loading" }
    | { status: "unavailable"; reason: "featureDisabled" | "manifestUnavailable" };

export const useMapStyle = (policy: BasemapPolicy): MapStyleResult => {
    const enabled = useFeature("selfHostedMapTiles");
    const [manifest, setManifest] = React.useState<BasemapManifest | null>(null);
    const [manifestFailed, setManifestFailed] = React.useState(false);
    React.useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        let active = true;
        loadBasemapManifest()
            .then(result => {
                if (active) {
                    setManifest(result);
                }
            })
            .catch((e: Error) => {
                reportError(e);
                if (active) {
                    setManifestFailed(true);
                }
            });
        return () => {
            active = false;
        };
    }, [enabled]);
    return React.useMemo((): MapStyleResult => {
        if (enabled && manifest) {
            ensurePmtilesProtocol();
            return { status: "ready", style: buildSelfHostedMapStyle(manifest.tiles) };
        }
        switch (policy) {
            case "allowThirdPartyFallback":
                return { status: "ready", style: MAPTILER_STYLE_URL };
            case "selfHostedOnly":
                if (!enabled) {
                    return { status: "unavailable", reason: "featureDisabled" };
                }
                if (manifestFailed) {
                    return { status: "unavailable", reason: "manifestUnavailable" };
                }
                return { status: "loading" };
            default: {
                const exhaustiveCheck: never = policy;
                throw new Error(`Unhandled basemap policy ${exhaustiveCheck}`);
            }
        }
    }, [enabled, manifest, manifestFailed, policy]);
};
